import { Controller } from '@hotwired/stimulus'

// Opens the Google Picker so the user can choose the bank statements (or, if folder-pick
// is verified to grant descendant access under drive.file, a whole folder) to pull into
// the system. Mirrors google_picker_controller but allows multi-select and PDF/CSV files.
export default class extends Controller {
  static values = {
    configUrl: String,
    folderSelect: Boolean,
  }

  static targets = ['driveFiles', 'fileName', 'pickButton']

  async pick() {
    this.pickButtonTarget.disabled = true

    try {
      const response = await fetch(this.configUrlValue, {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })

      if (!response.ok) {
        const body = await response.text()
        console.error(`[drive-import] picker config failed (${response.status}): ${body}`)
        return
      }

      const config = await response.json()

      await this.#loadGoogleApi()
      await this.#loadPicker()

      const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
      view.setMimeTypes('application/pdf,text/csv')
      view.setIncludeFolders(true)
      view.setSelectFolderEnabled(this.folderSelectValue)
      view.setMode(google.picker.DocsViewMode.LIST)
      view.setParent('root') // empieza en "Mi unidad" en vez de "Recientes"

      const picker = new google.picker.PickerBuilder()
        .addView(view)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(config.access_token)
        .setDeveloperKey(config.api_key)
        .setAppId(config.app_id)
        .setCallback(this.#pickerCallback.bind(this))
        .build()

      picker.setVisible(true)
    } catch (error) {
      console.error('[drive-import] failed to open Google Picker:', error)
    } finally {
      this.pickButtonTarget.disabled = false
    }
  }

  #pickerCallback(data) {
    if (data.action === google.picker.Action.PICKED) {
      const docs = data.docs.map((doc) => ({ id: doc.id, name: doc.name, mimeType: doc.mimeType }))

      this.driveFilesTarget.value = JSON.stringify(docs)
      this.fileNameTarget.textContent = docs.map((doc) => doc.name).join(', ')

      // Let the upload-mode coordinator enable the "Import from Drive" button.
      this.dispatch('filesChanged', { detail: { count: docs.length } })
    }
  }

  #loadGoogleApi() {
    if (window.gapi) return Promise.resolve()

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://apis.google.com/js/api.js'
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  #loadPicker() {
    return new Promise((resolve) => {
      gapi.load('picker', resolve)
    })
  }
}
